import {
  Avatar,
  Box,
  IconButton,
  Menu,
  MenuItem,
  Typography,
} from "@mui/material";
import React, { useMemo } from "react";
import { Logout as LogoutIcon, Settings as SettingsIcon, PersonOutline as PersonOutlineIcon } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useBoundStore } from "../../../../store";
import { isExpert } from "../../../../utils";
import { useUser } from "../../../../hooks/useUser";
import { useLogoutPath } from "../../../../hooks/useLogoutPath";

interface UserControlV2Props {
  // White-label surfaces (RMN portal) have no /settings route.
  hideSettings?: boolean;
}

export function UserControlV2({ hideSettings = false }: UserControlV2Props = {}) {
  const USER_MENU_WIDTH = "150px";
  const navigate = useNavigate();
  const { role } = useUser();
  // Derived, not passed in: the old `logoutTo` prop existed for exactly this
  // and no caller ever set it, so every logout landed on the generic /login.
  const logoutTo = useLogoutPath();
  const { user, logout } = useBoundStore((state) => ({
    user: state.user,
    logout: state.logout,
  }));
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);

  const handleMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };
  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogoutClick = async () => {
    await logout();
    handleClose();
    navigate(logoutTo);
  };

  const computedAvatar = useMemo(
    () => (user && user.avatar ? (user.avatar as string) : null),
    [user]
  );

  return (
    <Box>
      <IconButton
        size="large"
        aria-label="account of current user"
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={handleMenu}
        color="primary"
      >
        {computedAvatar ? (
          <Avatar src={computedAvatar} />
        ) : (
          <PersonOutlineIcon
            sx={{
              fontSize: 36,
              background: "var(--tint)",
              borderRadius: "50%",
              padding: 0.5,
            }}
          />
        )}
      </IconButton>
      <Menu
        id="menu-appbar"
        anchorEl={anchorEl}
        anchorOrigin={{
          vertical: "bottom",
          horizontal: "right",
        }}
        keepMounted
        open={Boolean(anchorEl)}
        onClose={handleClose}
        sx={{ marginTop: 3 }}
      >
        {!hideSettings && (
          <MenuItem
            sx={{ width: USER_MENU_WIDTH }}
            onClick={() => navigate("/settings")}
          >
            <SettingsIcon />
            <Typography>&nbsp;Settings</Typography>
          </MenuItem>
        )}

        <MenuItem
          sx={{ width: USER_MENU_WIDTH }}
          onClick={() => handleLogoutClick()}
        >
          <LogoutIcon color="error" />
          <Typography color="error">&nbsp;Log out</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
}
